import { useMemo } from 'react';
import Highcharts from 'highcharts';
import { useAppSelector, useWeekRange, useComparisonPeriod, WeekRange, ComparisonPeriod } from 'src/utils/Hooks';
import {
  getWeekIdRange,
  getFirstWeekIdOfYear,
  getLastWeekIdOfYear,
  yearPartOfWeekId
} from 'src/utils/dateUtils';
import { SplineChartV2Series } from './SplineChartV2';
import {
  SplineChartV2DataPointWithMeta,
  weekIdToMeta,
  weekIdToTimestamp,
  fillCategoryData,
  mergeComparisonAndPrimaryData
} from './splineChartV2Utils';


export interface UseSplineChartV2DataArgs {
  primarySeries: SplineChartV2Series;
  comparisonSeries?: SplineChartV2Series;
  forecastSeries?: SplineChartV2Series;
  /**
   * Optional explicit override for category start week ID.
   * When provided, bypasses automatic range detection entirely.
   */
  categoryStartWeekId?: number;
  /**
   * Optional explicit override for category end week ID.
   * Must be provided together with categoryStartWeekId.
   */
  categoryEndWeekId?: number;
  /**
   * Optional override for prior period detection.
   * When true, merges comparison and primary into one continuous line.
   * If undefined, detection is based on Redux comparisonTimePeriod.id.
   */
  isPriorPeriodOverride?: boolean;
}

export interface UseSplineChartV2DataResult {
  /** Whether we're in prior period mode (merged line) */
  isPriorPeriod: boolean;
  /** Merged data for prior period mode (comparison + primary) */
  mergedData: SplineChartV2DataPointWithMeta[] | null;
  /** Number of categories (buckets) */
  categoryCount: number;
  /** X-axis category labels */
  xAxisCategories: string[];
  /** Primary series data mapped to categories */
  primaryDataMapped: (SplineChartV2DataPointWithMeta | null)[];
  /** Comparison series data mapped to categories */
  comparisonDataMapped: (SplineChartV2DataPointWithMeta | null)[];
  /** Forecast series data mapped to categories */
  forecastDataMapped: (SplineChartV2DataPointWithMeta | null)[];
  /** Length of comparison data (for zone boundary) */
  comparisonLength: number;
  /**
   * Primary weekIds for each category index.
   * Used to determine the correct week label in tooltips when only comparison data exists.
   * This handles 52/53 week year mapping correctly (e.g., 202604 maps to 202505).
   */
  primaryWeekIdsForCategories: number[];
  /** The starting weekId for the primary period (used for computing expected weekId at any index) */
  primaryStartWeekId: number | undefined;
}

/**
 * Hook to transform series data for SplineChartV2.
 * Handles prior period detection, data merging, and category generation.
 */
const EMPTY_RESULT: UseSplineChartV2DataResult = {
  isPriorPeriod: false,
  mergedData: null,
  categoryCount: 0,
  xAxisCategories: [],
  primaryDataMapped: [],
  comparisonDataMapped: [],
  forecastDataMapped: [],
  comparisonLength: 0,
  primaryWeekIdsForCategories: [],
  primaryStartWeekId: undefined
};

export const useSplineChartV2Data = ({
  primarySeries,
  comparisonSeries,
  forecastSeries,
  categoryStartWeekId,
  categoryEndWeekId,
  isPriorPeriodOverride
}: UseSplineChartV2DataArgs): UseSplineChartV2DataResult => {
  // Get time period from Redux for dynamic category generation
  const defaultStartWeekId = useAppSelector((state) => state.mainTimePeriod.startWeek);
  const defaultEndWeekId = useAppSelector((state) => state.mainTimePeriod.endWeek);

  // Detect prior-period comparison from Redux state (unless overridden)
  const comparisonTimePeriodId = useAppSelector((state) => state.comparisonTimePeriod.id);
  const isPriorPeriodFromRedux = comparisonTimePeriodId === 'prior-period';
  const isPriorPeriod = isPriorPeriodOverride ?? isPriorPeriodFromRedux;

  // Week IDs in YYYYWW format are always > 100000. If they're not valid yet,
  // the async StacklineDate computation hasn't completed — return empty data
  // to prevent crashes during retailer switches or time period transitions.
  const isTimePeriodReady =
    (categoryStartWeekId > 0 || defaultStartWeekId > 100000) &&
    (categoryEndWeekId > 0 || defaultEndWeekId > 100000);

  // Detect whether we're in a full-year Prior Year view (YTD or Last Week + Prior Year).
  // Computed purely from URL query params — no API calls needed.
  const weekRange = useWeekRange();
  const comparisonPeriod = useComparisonPeriod();
  const isFullYearWithPriorYear =
    (weekRange === WeekRange.YTD || weekRange === WeekRange.LAST_WEEK) &&
    comparisonPeriod === ComparisonPeriod.PRIOR_YEAR;

  // For full-year mode, span the entire current primary year so both series render completely.
  // Explicit categoryStartWeekId/categoryEndWeekId props (e.g. MicroTrend) take priority.
  const primaryYear = isTimePeriodReady ? yearPartOfWeekId(defaultEndWeekId) : 0;
  const fullYearStartWeekId = isTimePeriodReady ? getFirstWeekIdOfYear(primaryYear) : 0;
  const fullYearEndWeekId = isTimePeriodReady ? getLastWeekIdOfYear(primaryYear) : 0;
  const startWeekId = categoryStartWeekId ?? (isFullYearWithPriorYear ? fullYearStartWeekId : defaultStartWeekId);
  const endWeekId = categoryEndWeekId ?? (isFullYearWithPriorYear ? fullYearEndWeekId : defaultEndWeekId);

  // Generate week IDs from time period (used for x-axis labels fallback)
  const weekIdsFromTimePeriod = useMemo(() => {
    if (!startWeekId || !endWeekId) return [];
    return getWeekIdRange(startWeekId, endWeekId);
  }, [startWeekId, endWeekId]);

  // For prior period: combine comparison + primary data into one continuous series
  // For forecasts with prior period (empty primary), we DON'T merge - instead we render
  // comparison and forecast as separate sequential series
  const hasForecastWithEmptyPrimary = isPriorPeriod && primarySeries.data.length === 0 && (forecastSeries?.data.length ?? 0) > 0;

  // Forecast with empty primary regardless of Prior Year vs Prior Period — use data-driven
  // category count and labels so the chart spans the actual data range (no blank space on right).
  const hasForecastWithEmptyPrimaryForLayout =
    primarySeries.data.length === 0 &&
    (forecastSeries?.data.length ?? 0) > 0 &&
    (comparisonSeries?.data.length ?? 0) > 0;

  // For FULL_YEAR + PRIOR_PERIOD: we have all three data sources (comparison, primary, forecast)
  // In this case, the forecast should be positioned after the merged (comparison + primary) data
  const hasForecastWithPrimaryInPriorPeriod = isPriorPeriod && primarySeries.data.length > 0 && (forecastSeries?.data.length ?? 0) > 0;
  
  // For standard mode (PRIOR_YEAR comparison): when we have both primary and forecast data,
  // the forecast should continue from where primary ends (with an overlap point for connection)
  const hasForecastWithPrimaryInStandardMode = !isPriorPeriod && primarySeries.data.length > 0 && (forecastSeries?.data.length ?? 0) > 0;
  
  const mergedData = useMemo(() => {
    if (!isPriorPeriod || !comparisonSeries) return null;
    // Don't merge if we're in forecast mode with empty primary - render as sequential series instead
    if (hasForecastWithEmptyPrimary) return null;
    return mergeComparisonAndPrimaryData(comparisonSeries.data, primarySeries.data);
  }, [isPriorPeriod, comparisonSeries, primarySeries.data, hasForecastWithEmptyPrimary]);

  // Prior Period with empty primary and forecast: the forecast series has a prepended connection
  // point (last comparison value) so the dashed line connects to the solid line. This flag
  // controls special handling in categoryCount, labels, and data mapping for that case.
  // Note: when true, hasForecastWithEmptyPrimary is also true, but this flag takes precedence
  // in the hasForecastWithEmptyPrimaryForLayout branches (checked first).
  const hasForecastConnectionPoint = hasForecastWithEmptyPrimaryForLayout && isPriorPeriod;

  // Calculate bucket count
  const categoryCount = useMemo(() => {
    // Forecast with empty primary: Prior Period = comparison + forecast (connected); Prior Year = forecast only.
    if (hasForecastWithEmptyPrimaryForLayout) {
      const forecastLength = forecastSeries?.data.length ?? 0;
      const comparisonLen = comparisonSeries?.data.length ?? 0;
      if (hasForecastConnectionPoint) {
        // Prior Period: one shared connection bucket (like non-forecast 1:1 categories) → no duplicate week labels
        return comparisonLen + Math.max(0, forecastLength - 1);
      }
      return forecastLength;
    }
    if (isPriorPeriod && comparisonSeries) {
      if (hasForecastWithEmptyPrimary) {
        // For forecasts with prior period (empty primary): comparison + forecast (minus 1 for overlapping point)
        const forecastLength = forecastSeries?.data.length ?? 0;
        return comparisonSeries.data.length + Math.max(0, forecastLength - 1);
      }
      if (hasForecastWithPrimaryInPriorPeriod) {
        // For FULL_YEAR + PRIOR_PERIOD: comparison + primary + forecast (minus 1 for overlapping point at forecast start)
        const forecastLength = forecastSeries?.data.length ?? 0;
        return comparisonSeries.data.length + primarySeries.data.length + Math.max(0, forecastLength - 1);
      }
      // Standard prior period: comparison + primary lengths
      return comparisonSeries.data.length + primarySeries.data.length;
    }
    
    // Standard mode with forecast: primary + forecast (minus 1 for overlapping connection point).
    // Do not exceed primary + forecast length so lines overlap at the join.
    if (hasForecastWithPrimaryInStandardMode) {
      const forecastLength = forecastSeries?.data.length ?? 0;
      return primarySeries.data.length + Math.max(0, forecastLength - 1);
    }
    
    // When forecast is present, bucket count = primary + forecast - 1 (overlap); never inflate with Redux range.
    const forecastLength = forecastSeries?.data.length ?? 0;
    if (forecastLength > 0) {
      return primarySeries.data.length + Math.max(0, forecastLength - 1);
    }
    
    // Standard mode without forecast: max of series lengths or time period
    const lengths = [
      primarySeries.data.length,
      comparisonSeries?.data.length ?? 0,
      forecastSeries?.data.length ?? 0
    ];
    return Math.max(...lengths, weekIdsFromTimePeriod.length);
  }, [hasForecastWithEmptyPrimaryForLayout, hasForecastConnectionPoint, isPriorPeriod, comparisonSeries, primarySeries.data.length, forecastSeries?.data.length, weekIdsFromTimePeriod.length, hasForecastWithEmptyPrimary, hasForecastWithPrimaryInPriorPeriod, hasForecastWithPrimaryInStandardMode]);

  // Generate x-axis category labels
  const xAxisCategories = useMemo(() => {
    // Forecast with empty primary: Prior Period = comparison labels then forecast (skip overlap); Prior Year = forecast only.
    if (hasForecastWithEmptyPrimaryForLayout && forecastSeries) {
      if (hasForecastConnectionPoint && comparisonSeries) {
        // One label per category: (N-1) comparison + M forecast so connection bucket = first forecast week only (no duplicate week 7)
        const labels: string[] = [];
        comparisonSeries.data.slice(0, -1).forEach((point) => {
          labels.push(Highcharts.dateFormat('%b %e', weekIdToTimestamp(point.weekId)));
        });
        forecastSeries.data.forEach((point) => {
          labels.push(Highcharts.dateFormat('%b %e', weekIdToTimestamp(point.weekId)));
        });
        return labels;
      }
      return forecastSeries.data.map((point) =>
        Highcharts.dateFormat('%b %e', weekIdToTimestamp(point.weekId))
      );
    }
    if (isPriorPeriod && mergedData) {
      // Merged mode: labels from comparison weekIds + primary weekIds
      const labels = mergedData.map((point) => {
        const ts = weekIdToTimestamp(point.weekId);
        return Highcharts.dateFormat('%b %e', ts);
      });
      
      // For FULL_YEAR + PRIOR_PERIOD: also add forecast labels (skip first overlapping point)
      if (hasForecastWithPrimaryInPriorPeriod && forecastSeries) {
        forecastSeries.data.slice(1).forEach((point) => {
          const ts = weekIdToTimestamp(point.weekId);
          labels.push(Highcharts.dateFormat('%b %e', ts));
        });
      }
      
      return labels;
    }
    
    // For forecasts with prior period (empty primary): sequential comparison + forecast labels
    if (hasForecastWithEmptyPrimary && comparisonSeries) {
      const labels: string[] = [];
      
      // Add comparison labels first
      comparisonSeries.data.forEach((point) => {
        const ts = weekIdToTimestamp(point.weekId);
        labels.push(Highcharts.dateFormat('%b %e', ts));
      });
      
      // Add forecast labels (skip first point as it overlaps with last comparison point)
      if (forecastSeries) {
        forecastSeries.data.slice(1).forEach((point) => {
          const ts = weekIdToTimestamp(point.weekId);
          labels.push(Highcharts.dateFormat('%b %e', ts));
        });
      }
      
      return labels;
    }
    
    // Standard mode with forecast: primary labels + forecast labels (skip overlap point)
    if (hasForecastWithPrimaryInStandardMode && forecastSeries) {
      const labels: string[] = [];
      
      // Add primary labels first
      primarySeries.data.forEach((point) => {
        const ts = weekIdToTimestamp(point.weekId);
        labels.push(Highcharts.dateFormat('%b %e', ts));
      });
      
      // Add forecast labels (skip first point as it overlaps with last primary point)
      forecastSeries.data.slice(1).forEach((point) => {
        const ts = weekIdToTimestamp(point.weekId);
        labels.push(Highcharts.dateFormat('%b %e', ts));
      });
      
      return labels;
    }
    
    // Standard mode: generate labels from available data
    // Use whichever series has data at each index (primary > comparison > forecast > fallback)
    return Array.from({ length: categoryCount }, (_, idx) => {
      // Try primary series first
      const primaryPoint = primarySeries.data[idx];
      if (primaryPoint) {
        const ts = weekIdToTimestamp(primaryPoint.weekId);
        return Highcharts.dateFormat('%b %e', ts);
      }
      
      // Fall back to comparison series (for Prior Year where primary is empty)
      const comparisonPoint = comparisonSeries?.data[idx];
      if (comparisonPoint) {
        const ts = weekIdToTimestamp(comparisonPoint.weekId);
        return Highcharts.dateFormat('%b %e', ts);
      }
      
      // Fall back to forecast series
      const forecastPoint = forecastSeries?.data[idx];
      if (forecastPoint) {
        const ts = weekIdToTimestamp(forecastPoint.weekId);
        return Highcharts.dateFormat('%b %e', ts);
      }
      
      // Last resort: use Redux time period
      const weekId = weekIdsFromTimePeriod[idx];
      if (weekId) {
        const ts = weekIdToTimestamp(weekId);
        return Highcharts.dateFormat('%b %e', ts);
      }
      return '';
    });
  }, [hasForecastWithEmptyPrimaryForLayout, isPriorPeriod, mergedData, categoryCount, primarySeries.data, weekIdsFromTimePeriod, hasForecastWithEmptyPrimary, hasForecastWithPrimaryInPriorPeriod, hasForecastWithPrimaryInStandardMode, comparisonSeries, forecastSeries]);

  // Fill each category position sequentially with data
  const primaryDataMapped = useMemo(
    () => fillCategoryData(primarySeries.data, categoryCount),
    [primarySeries.data, categoryCount]
  );

  // For forecasts with prior period: comparison fills first N positions, then nulls
  const comparisonDataMapped = useMemo(() => {
    if (!comparisonSeries) return [];
    
    if (hasForecastConnectionPoint) {
      // Prior Period: keep all comparison points including week 7 (no slice).
      const comparisonLen = comparisonSeries.data.length;
      const mapped = comparisonSeries.data.map((point) => ({
        ...point,
        meta: weekIdToMeta(point.weekId)
      }));
      while (mapped.length < categoryCount) {
        mapped.push(null);
      }
      return mapped;
    }
    if (hasForecastWithEmptyPrimaryForLayout || hasForecastWithEmptyPrimary) {
      const mapped = comparisonSeries.data
        .slice(0, categoryCount)
        .map((point) => ({
          ...point,
          meta: weekIdToMeta(point.weekId)
        }));
      while (mapped.length < categoryCount) {
        mapped.push(null);
      }
      return mapped;
    }
    
    return fillCategoryData(comparisonSeries.data, categoryCount);
  }, [comparisonSeries, categoryCount, hasForecastWithEmptyPrimaryForLayout, hasForecastWithEmptyPrimary, hasForecastConnectionPoint]);

  // For forecasts with prior period: forecast fills positions after comparison (or after primary for FULL_YEAR)
  const forecastDataMapped = useMemo(() => {
    if (!forecastSeries) return [];
    
    // Forecast with empty primary: Prior Period = nulls then forecast (connected at overlap); Prior Year = all forecast.
    if (hasForecastWithEmptyPrimaryForLayout) {
      if (hasForecastConnectionPoint && comparisonSeries) {
        // One category per week. Connection bucket = first forecast week — use actual forecast data for that week (e.g. week 7 2026).
        const comparisonLen = comparisonSeries.data.length;
        const mapped: (SplineChartV2DataPointWithMeta | null)[] = Array(comparisonLen - 1).fill(null);
        forecastSeries.data.forEach((point) => {
          mapped.push({
            ...point,
            value: point.value ?? null,
            meta: weekIdToMeta(point.weekId)
          });
        });
        return mapped;
      }
      const source = forecastSeries.data;
      return source.map((point) => ({
        ...point,
        meta: weekIdToMeta(point.weekId)
      }));
    }
    if ((hasForecastWithEmptyPrimary) && comparisonSeries) {
      const comparisonLen = comparisonSeries.data.length;
      const mapped: (SplineChartV2DataPointWithMeta | null)[] = Array(comparisonLen - 1).fill(null);
      forecastSeries.data.forEach((point) => {
        mapped.push({ ...point, meta: weekIdToMeta(point.weekId) });
      });
      return mapped;
    }
    
    if (hasForecastWithPrimaryInPriorPeriod && comparisonSeries) {
      // For FULL_YEAR + PRIOR_PERIOD: forecast starts after merged (comparison + primary) data
      // Start with nulls for comparison + primary portion (minus 1 for overlap point)
      const comparisonLen = comparisonSeries.data.length;
      const primaryLen = primarySeries.data.length;
      const offsetLength = comparisonLen + primaryLen - 1;
      const mapped: (SplineChartV2DataPointWithMeta | null)[] = Array(offsetLength).fill(null);
      
      // Add forecast data starting at the overlap point (skip first point as it overlaps with last primary)
      forecastSeries.data.forEach((point) => {
        mapped.push({
          ...point,
          meta: weekIdToMeta(point.weekId)
        });
      });
      
      return mapped;
    }
    
    // Standard mode with primary + forecast: forecast starts after primary data (with overlap point)
    if (hasForecastWithPrimaryInStandardMode) {
      const primaryLen = primarySeries.data.length;
      // Start with nulls for positions before the overlap point (primaryLen - 1 nulls)
      const mapped: (SplineChartV2DataPointWithMeta | null)[] = Array(primaryLen - 1).fill(null);
      
      // Add forecast data starting at the overlap point
      forecastSeries.data.forEach((point) => {
        mapped.push({
          ...point,
          meta: weekIdToMeta(point.weekId)
        });
      });
      
      return mapped;
    }
    
    return fillCategoryData(forecastSeries.data, categoryCount);
  }, [forecastSeries, categoryCount, hasForecastWithEmptyPrimaryForLayout, hasForecastWithEmptyPrimary, hasForecastWithPrimaryInPriorPeriod, hasForecastWithPrimaryInStandardMode, comparisonSeries, primarySeries.data.length]);

  const comparisonLength = comparisonSeries?.data.length ?? 0;

  // Primary weekIds for each category index - used for tooltip week label and comparison pairing.
  // Must match the "main" period so tooltip shows correct week and comparison is 1:1 (same as old useForecastMetricByWeekId).
  const primaryWeekIdsForCategories = useMemo(() => {
    // Forecast with empty primary: Prior Period = comparison weekIds then forecast (skip overlap); Prior Year = all forecast weekIds.
    if (hasForecastWithEmptyPrimaryForLayout && forecastSeries) {
      if (hasForecastConnectionPoint && comparisonSeries) {
        // One weekId per category: (N-1) comparison + M forecast (connection bucket = first forecast week)
        const fromComparison = comparisonSeries.data.slice(0, -1).map((p) => p.weekId);
        const fromForecast = forecastSeries.data.map((p) => p.weekId);
        return [...fromComparison, ...fromForecast];
      }
      return forecastSeries.data.map((p) => p.weekId);
    }
    return weekIdsFromTimePeriod;
  }, [hasForecastWithEmptyPrimaryForLayout, hasForecastConnectionPoint, forecastSeries, comparisonSeries, weekIdsFromTimePeriod]);

  if (!isTimePeriodReady) {
    return EMPTY_RESULT;
  }

  return {
    isPriorPeriod,
    mergedData,
    categoryCount,
    xAxisCategories,
    primaryDataMapped,
    comparisonDataMapped,
    forecastDataMapped,
    comparisonLength,
    primaryWeekIdsForCategories,
    primaryStartWeekId: startWeekId
  };
};

export default useSplineChartV2Data;
